import React, { useEffect, useState, useRef } from 'react';
import { Menu, X, ArrowRight, Mail, Instagram } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

// Data reused for Footer/Navigation consistency
const services = [
    { title: 'Website Design and Development', link: '/web-development' },
    { title: 'Website Revamp', link: '/website-revamp' },
    { title: 'Server Hosting & Maintenance', link: '/hosting-maintenance' },
];

// --- Reuse FooterSection to maintain consistency ---
const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-12 md:py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 md:gap-12 border-b border-slate-700 pb-12">
                    {/* Brand & Contact */}
                    <div className="sm:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        <div className="flex space-x-4 pt-2">
                            <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                            <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                        </ul>
                    </div>

                    {/* Services */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Contact */}
                    <div className="space-y-4 col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors break-all">vyxionlabs.info@gmail.com</a>
                            </li>
                        </ul>
                    </div>
                </div>
                
                {/* Legal Links */}
                <div className="flex flex-col md:flex-row justify-center md:justify-start gap-4 md:space-x-6 mt-8 items-center md:items-start">
                    
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>

                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};

export default function PrivacyPolicy() {
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const servicesDropdownRef = useRef(null); 

  const handleServicesClick = (e) => {
    e.stopPropagation(); 
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [servicesDropdownRef]);

  const handleMenuClick = () => setIsContactPopupOpen(!isContactPopupOpen);

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 text-slate-900 min-h-screen antialiased relative overflow-x-hidden flex flex-col">
      <style>{`
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        /* Float animation removed, but shimmer kept as requested */
        
        .modal-overlay {
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(8px);
            z-index: 9999; opacity: 0; visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .modal-overlay.open { opacity: 1; visibility: visible; }
        .modal-content {
            position: fixed; top: 0; right: 0; width: 100%; max-width: 400px; height: 100%;
            background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(30px);
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2); transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
            overflow-y: auto; padding: 2rem 1.5rem;
        }
        .modal-overlay.open .modal-content { transform: translateX(0); }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
          background-size: 1000px 100%; animation: shimmer 3s infinite;
        }
        .glass {
          background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
        }
        
        .policy-content h1 { font-size: 2rem; font-weight: 800; margin-bottom: 1.5rem; color: #0f172a; }
        .policy-content h2 { font-size: 1.5rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; color: #1e293b; }
        .policy-content h3 { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #334155; }
        .policy-content p { margin-bottom: 1rem; line-height: 1.75; color: #475569; }
        .policy-content ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem; color: #475569; }
        .policy-content li { margin-bottom: 0.5rem; }
        .policy-content a { color: #0891b2; text-decoration: underline; }
        .policy-content strong { color: #0f172a; font-weight: 600; }
      `}</style>


      {/* Header */}
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox Layout */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                   <div className="flex flex-col p-2">
                       <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm bg-neutral-100 transition-colors text-sm font-bold text-neutral-900">
                           Website Design & Development
                       </a>
                       <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Revamp
                       </a>
                       <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Server Hosting & Maintenance
                       </a>
                   </div>
               </div>
               
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium">About Us</a>
                <a href="/ourworks" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-700 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Contact Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100">
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-400" onClick={handleMenuClick}>About Us</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
            <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
           
        </div>
      </div>

      <main className="flex-grow pt-10 pb-20 px-4 md:px-6 z-10">
        <div className="max-w-4xl mx-auto">
            
            {/* Page Header - Static */}
            <div id="policy-header" className="text-center mb-12">
                <h1 className="text-4xl md:text-6xl font-black mb-4 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                    Privacy Policy
                </h1>
            </div>

            {/* Content Container - Static (no slide-in) */}
            <div 
                id="policy-content" 
                className="glass rounded-3xl p-8 md:p-12 shadow-2xl policy-content"
            >
                <div className="prose prose-slate max-w-none">
                    <h2>PRIVACY POLICY</h2>
                    <p><strong>Last updated December 21, 2025</strong></p>

                    <p>This Privacy Notice for Vyxion Labs ('we', 'us', or 'our'), describes how and why we might access, collect, store, use, and/or share ('process') your personal information when you use our services ('Services'), including when you:</p>

                    <ul>
                        <li>Visit our website at <a href="https://vyxionlabs.com" target="_blank" rel="noopener noreferrer">https://vyxionlabs.com</a> or any website of ours that links to this Privacy Notice</li>
                        <li>Use Website Design Service. We provide end-to-end website solutions — from designing and developing custom websites, revamping existing ones, to hosting and ongoing maintenance — ensuring your online presence is seamless, professional, and built for growth.</li>
                        <li>Engage with us in other related ways, including any marketing or events</li>
                    </ul>

                    <p><strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy rights and choices. If you still have any questions or concerns, please contact us at <a href="mailto:vyxionlabs.info@gmail.com">vyxionlabs.info@gmail.com</a>.</p>

                    <h2>SUMMARY OF KEY POINTS</h2>
                    
                    <p><em>This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by using our table of contents below.</em></p>

                    <p><strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services.</p>

                    <p><strong>Do we process any sensitive personal information?</strong> We do not process sensitive personal information.</p>

                    <p><strong>Do we collect any information from third parties?</strong> We may collect information from public databases, marketing partners, social media platforms, and other outside sources.</p>

                    <p><strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</p>

                    <h2>TABLE OF CONTENTS</h2>
                    
                    <ol>
                        <li>WHAT INFORMATION DO WE COLLECT?</li>
                        <li>HOW DO WE PROCESS YOUR INFORMATION?</li>
                        <li>WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
                        <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
                        <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
                        <li>HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
                        <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
                        <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
                        <li>CONTROLS FOR DO-NOT-TRACK FEATURES</li>
                        <li>DO WE MAKE UPDATES TO THIS NOTICE?</li>
                        <li>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
                        <li>HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</li>
                    </ol>

                    <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
                    
                    <h3>Personal information you disclose to us</h3>
                    <p><em>In Short: We collect personal information that you provide to us.</em></p>
                    
                    <p>We collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.</p>

                    <p><strong>Personal Information Provided by You.</strong> The personal information we collect may include:</p>
                    <ul>
                        <li>names</li>
                        <li>phone numbers</li>
                        <li>email addresses</li>
                        <li>contact preferences</li>
                    </ul>

                    <p><strong>Sensitive Information.</strong> We do not process sensitive information.</p>

                    <h3>Information automatically collected</h3>
                    <p><em>In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.</em></p>

                    <p>We automatically collect certain information when you visit, use, or navigate the Services. This information includes device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, and other technical information.</p>

                    <p>The information we collect includes:</p>
                    <ul>
                        <li><em>Log and Usage Data.</em> Log and usage data includes diagnostic, usage, and performance information our servers automatically collect.</li>
                        <li><em>Device Data.</em> We collect device data such as information about your computer, phone, tablet, or other device you use to access the Services.</li>
                        <li><em>Location Data.</em> We collect location data such as information about your device's location, which can be either precise or imprecise.</li>
                    </ul>

                    <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
                    <p><em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</em></p>

                    <p><strong>We process your personal information for a variety of reasons, including:</strong></p>
                    <ul>
                        <li><strong>To deliver and facilitate delivery of services to the user.</strong> We may process your information to provide you with the requested service.</li>
                        <li><strong>To respond to user inquiries/offer support to users.</strong> We may process your information to respond to your inquiries and solve any potential issues.</li>
                        <li><strong>To send administrative information to you.</strong> We may process your information to send you details about our products and services.</li>
                        <li><strong>To request feedback.</strong> We may process your information when necessary to request feedback about your use of our Services.</li>
                        <li><strong>To deliver targeted advertising to you.</strong> We may process your information to develop and display personalised content and advertising.</li>
                        <li><strong>To protect our Services.</strong> We may process your information as part of our efforts to keep our Services safe and secure.</li>
                        <li><strong>To evaluate and improve our Services.</strong> We may process your information to identify usage trends and determine the effectiveness of our campaigns.</li>
                    </ul>

                    <h2>3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
                    <p><em>In Short: We may share information in specific situations and with specific categories of third parties.</em></p>

                    <p><strong>Vendors, Consultants, and Other Third-Party Service Providers.</strong> We may share your data with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf.</p>

                    <p>The categories of third parties we may share personal information with are:</p>
                    <ul>
                        <li>Sales & Marketing Tools</li>
                        <li>Payment Processors</li>
                        <li>Website Hosting Service Providers</li>
                    </ul>

                    <p>We also may need to share your personal information in the following situations:</p>
                    <ul>
                        <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with any merger, sale of company assets, financing, or acquisition.</li>
                    </ul>

                    <h2>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
                    <p><em>In Short: We may use cookies and other tracking technologies to collect and store your information.</em></p>

                    <p>We may use cookies and similar tracking technologies to gather information when you interact with our Services.</p>

                    <h3>Google Analytics</h3>
                    <p>We may share your information with Google Analytics to track and analyse the use of the Services. To opt out of being tracked by Google Analytics, visit <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a>.</p>

                    <h2>5. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
                    <p><em>In Short: We keep your information for as long as necessary to fulfil the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>

                    <p>We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice.</p>

                    <h2>6. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
                    <p><em>In Short: We aim to protect your personal information through a system of organisational and technical security measures.</em></p>

                    <p>We have implemented appropriate technical and organisational security measures designed to protect your information. However, no electronic transmission can be guaranteed to be 100% secure.</p>

                    <h2>7. DO WE COLLECT INFORMATION FROM MINORS?</h2>
                    <p><em>In Short: We do not knowingly collect data from or market to children under 18 years of age.</em></p>

                    <p>We do not knowingly collect data from children under 18 years of age. If you become aware of any data we may have collected from children, please contact us at <a href="mailto:vyxionlabs.info@gmail.com">vyxionlabs.info@gmail.com</a>.</p>

                    <h2>8. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
                    <p><em>In Short: You may review, change, or terminate your account at any time.</em></p>

                    <p><strong>Withdrawing your consent:</strong> If we are relying on your consent to process your personal information, you have the right to withdraw your consent at any time by contacting us.</p>

                    <p>If you have questions about your privacy rights, you may email us at <a href="mailto:vyxionlabs.info@gmail.com">vyxionlabs.info@gmail.com</a>.</p>

                    <h2>9. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
                    <p>Most web browsers include a Do-Not-Track ('DNT') feature. We do not currently respond to DNT browser signals.</p>

                    <h2>10. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
                    <p><em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>

                    <p>We may update this Privacy Notice from time to time. The updated version will be indicated by an updated 'Revised' date at the top of this Privacy Notice.</p>

                    <h2>11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
                    <p>If you have questions or comments about this notice, you may email us at <a href="mailto:vyxionlabs.info@gmail.com">vyxionlabs.info@gmail.com</a></p>

                    <h2>12. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h2>
                    <p>Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you. To request to review, update, or delete your personal information, please fill out and submit a <a href="https://app.termly.io/dsar/4facdcf3-dfe9-4462-8841-7371042aadfa" target="_blank" rel="noopener noreferrer">data subject access request</a>.</p>
                </div>
            </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}